"use client";

import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import type {
  AimOfAnalysis,
  AudienceBackground,
} from "@/generated/fastapi";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import LoadingState from "@/shared/ui/LoadingState";
import GenericCopyClipboard from "@/shared/ui/actions/GenericCopyClipboard";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import GenericToggle from "@/shared/ui/controls/GenericToggle";
import GenericCollapsible from "@/shared/ui/display/GenericCollapsible";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping";
import TopicReviewItem from "./TopicReviewItem";
import useIdentifyTopicsController from "./useIdentifyTopicsController";

type IdentifyTopicsWorkflowProps = {
  cancelLabel?: string;
  originId: string;
  onCancel?: () => void;
  showOrientation?: boolean;
};

export default function IdentifyTopicsWorkflow({
  cancelLabel = "Return",
  originId,
  onCancel,
  showOrientation = true,
}: IdentifyTopicsWorkflowProps) {
  const controller = useIdentifyTopicsController({ originId });

  if (controller.status.isLoading) {
    return (
      <LoadingState message="The system is analyzing the content to find useful topics. This may take a few moments." />
    );
  }

  return (
    <Box>
      <VStack align="stretch">
        {showOrientation ? (
          <GenericDrawerInfo
            description="Find topics in this source material, then confirm useful topics or propose drafts."
            items={controller.drawerInfoItems}
          />
        ) : null}

        <VStack align="stretch" gap="2">
          <GenericSelector<AimOfAnalysis>
            previewItems={controller.controls.aimItems}
            stateObject={controller.controls.selectedAims}
            stateObjectSetter={controller.controls.setSelectedAims}
            placeholder="Aim of analysis to focus on"
            disabled={controller.review.hasUnconfirmedTopics}
          />

          <HStack gap="2" align="center" justify="space-between" width="full">
            {onCancel ? (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                aria-label={`${cancelLabel} identify topics`}
                minW={{ base: "10", sm: "auto" }}
              >
                <GenericIcon icon="return" />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  {cancelLabel}
                </Text>
              </Button>
            ) : (
              <Box />
            )}

            <HStack gap="2" align="center" justify="flex-end">
              <Button
                type="button"
                variant="outline"
                onClick={controller.controls.handleToggleOptions}
                aria-label="Toggle additional options"
                minW={{ base: "10", sm: "auto" }}
              >
                <GenericIcon
                  icon="adjust"
                  highlighted={controller.controls.hasCustomOptions}
                />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  Options
                </Text>
              </Button>

              <Button
                type="button"
                bg="moveon.primary"
                onClick={controller.controls.handleIdentify}
                loading={controller.controls.isIdentifying}
                loadingText="Finding..."
                disabled={
                  !controller.controls.canIdentify ||
                  controller.controls.isIdentifying
                }
                aria-label="Find topics from content"
                minW={{ base: "10", sm: "auto" }}
              >
                <GenericIcon icon="identify_topics" />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  {PROFILE_SHAPING_LABELS.findTopics}
                </Text>
              </Button>
            </HStack>
          </HStack>

          {controller.controls.disabledReason ? (
            <Text fontSize="xs" color="moveon.text.muted">
              {controller.controls.disabledReason}
            </Text>
          ) : null}
        </VStack>

        {controller.options.isOpen && (
          <GenericCollapsible
            isOpen={controller.options.isOpen}
            setIsOpenAction={controller.options.setIsOpen}
            setChildrenBorder={true}
          >
            <VStack align="stretch" spaceY="4">
              <GenericSelector<AudienceBackground>
                previewItems={controller.options.audienceBackgroundItems}
                stateObject={controller.options.selectedAudienceBackground}
                stateObjectSetter={
                  controller.options.setSelectedAudienceBackground
                }
                placeholder="Select Audience Background (optional)"
              />
              <Input
                placeholder="Intended message to convey (optional)"
                value={controller.options.intendedMessage}
                onChange={(e) =>
                  controller.options.setIntendedMessage(e.target.value)
                }
              />
              <GenericToggle
                label="Associate with MoveOn"
                value={controller.options.associateWithMoveOn}
                onChange={(newValue) => {
                  controller.options.setAssociateWithMoveOn(newValue);
                }}
              />
            </VStack>
          </GenericCollapsible>
        )}
      </VStack>

      {controller.review.isIdentifiedTopics ? (
        <VStack align="stretch" mt="4">
          <HStack justify="space-between">
            <Heading size="sm">
              {controller.review.hasUnconfirmedTopics
                ? `Identified Topics (${controller.review.identifiedTopics!.length})`
                : `Confirmed Topics (${controller.review.identifiedTopics!.length})`}
            </Heading>
            {controller.review.proposedPost && (
              <GenericCopyClipboard
                stringToCopy={
                  controller.review.proposedPost ||
                  "No proposed post available yet."
                }
                iconSize="md"
              />
            )}
            {controller.review.hasUnconfirmedTopics && (
              <Button
                variant="ghost"
                onClick={() => controller.review.removeAllTopicsFromLocalList()}
              >
                <GenericIcon icon="reset" />
              </Button>
            )}
          </HStack>

          <Spacer />
          {controller.review.identifiedTopics!.map((topic) => (
            <TopicReviewItem
              key={topic.id}
              topic={topic}
              removeTopic={() => controller.review.removeTopic(topic.id)}
              updateTopic={(update) =>
                controller.review.updateTopic(topic.id, update)
              }
              setProposedPost={controller.review.setProposedPost}
              inConfirmationAfterExtraction={
                controller.review.hasUnconfirmedTopics
              }
            />
          ))}
        </VStack>
      ) : (
        <GenericEmptyComponent
          title="No Topics Identified"
          message="No topics have been identified yet. Please click the button above to start the identification process."
        />
      )}
    </Box>
  );
}
